import React, { useEffect, useState } from "react";
import { Plus, Terminal, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { CopyButton, StatusPill } from "@/components/common";

export default function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState(null);

  const load = () => api.get("/api-keys").then((r) => setKeys(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/api-keys", { name: name.trim() });
      setReveal(data);
      setName("");
      load();
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this key? Apps using it will stop working.")) return;
    await api.delete(`/api-keys/${id}`);
    toast.success("Key revoked");
    load();
  };

  return (
    <div className="space-y-6 fade-up">
      <div>
        <h1 className="display text-2xl font-bold">API Keys</h1>
        <p className="mt-1 text-sm text-slate-400">Authenticate REST API requests with <code className="mono text-emerald-400">X-API-Key</code>.</p>
      </div>

      <form onSubmit={create} className="flex gap-3 rounded-xl border border-slate-800 bg-slate-900/40 p-4">
        <input data-testid="api-key-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Key name (e.g. Backend server)"
          className="flex-1 rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2.5 text-sm outline-none focus:border-emerald-500/60" />
        <button data-testid="api-key-generate-button" disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-[#04120c] hover:bg-emerald-400 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Generate key
        </button>
      </form>

      {reveal && (
        <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-5 glow-emerald" data-testid="apikey-reveal">
          <div className="flex items-center gap-2 text-sm font-semibold text-emerald-300"><Terminal className="h-4 w-4" /> Copy your key now — it won't be shown again</div>
          <div className="mt-3 flex items-center gap-2">
            <code className="mono flex-1 break-all rounded-lg border border-slate-800 bg-slate-950 p-3 text-xs text-emerald-400">{reveal.key}</code>
            <CopyButton value={reveal.key} label="API key" testid="apikey-copy" />
          </div>
          <button onClick={() => setReveal(null)} className="mt-3 text-xs text-slate-400 hover:text-white">Dismiss</button>
        </div>
      )}

      <div className="space-y-3">
        {keys.length === 0 && <div className="rounded-xl border border-dashed border-slate-800 py-12 text-center text-sm text-slate-500">No API keys yet.</div>}
        {keys.map((k) => (
          <div key={k.id} data-testid={`apikey-row-${k.id}`} className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-900/40 px-5 py-4">
            <div className="flex items-center gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-700 bg-slate-800/60 text-emerald-400"><Terminal className="h-5 w-5" /></div>
              <div>
                <div className="font-medium">{k.name}</div>
                <div className="mono text-xs text-slate-500">{k.key_prefix}•••• · {k.last_used ? `used ${new Date(k.last_used).toLocaleDateString()}` : "never used"}</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <StatusPill status={k.status} />
              {k.status === "active" && <button onClick={() => revoke(k.id)} data-testid={`apikey-revoke-${k.id}`} className="text-xs font-medium text-red-400 hover:underline">Revoke</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
